/**
 * EditorShell — общий каркас редактора виджета. См. JSDoc-контракт в
 * `editor-shell.tsx` — здесь только реализация. Body передаётся через `body` prop.
 *
 * Layout:
 *   ┌─────────────────────────────┬─────────────────────────────┐
 *   │  [Cancel] [Save] [Save&Cl] [Preview Page]                │
 *   ├──────────┬──────────────────┼─────────────────────────────┤
 *   │ tabs ↓   │  body.renderTab  │  Preview state ▾            │
 *   │ content  │  (per-type form) │  [LivePreview]              │
 *   │ theme    │                  │                             │
 *   │ behavior │                  │                             │
 *   │ code     │                  │                             │
 *   └──────────┴──────────────────┴─────────────────────────────┘
 */
import { useEffect, useState } from 'react';

import { type WidgetBodyContract, type WidgetDraft } from './editor-shell';
import { LivePreview, type PreviewState } from './live-preview';
import { ensureThemesLoaded } from './themes-loader';

interface Props {
  draft: WidgetDraft;
  body: WidgetBodyContract;
  onChange: (next: WidgetDraft) => void;
  onSave: (closeAfter: boolean) => Promise<void> | void;
  onCancel: () => void;
  onOpenPreviewPage?: () => void;
  /** Состояния preview, доступные для текущего типа. Если null — дефолтный список. */
  previewStates?: PreviewState[];
}

const DEFAULT_PREVIEW_STATES: Record<string, PreviewState[]> = {
  chat: ['collapsed', 'notification', 'expanded', 'offline'],
  form: ['expanded', 'success'],
  survey: ['expanded', 'success'],
  promo: ['expanded'],
};

export function EditorShell({
  draft,
  body,
  onChange,
  onSave,
  onCancel,
  onOpenPreviewPage,
}: Props): JSX.Element {
  const tabs = body.getTabs();
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.key ?? '');
  const states = DEFAULT_PREVIEW_STATES[draft.type] ?? ['expanded'];
  const [previewState, setPreviewState] = useState<PreviewState>(states[0] ?? 'expanded');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void ensureThemesLoaded();
  }, []);

  const errors = body.validate(draft);
  const handleSave = async (closeAfter: boolean): Promise<void> => {
    if (errors.length > 0 || saving) return;
    setSaving(true);
    try {
      await onSave(closeAfter);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inbx-widget-editor">
      <div className="inbx-widget-editor__toolbar">
        {/* Сохранение слева, закрытие (×) справа — привычный порядок. */}
        <div className="inbx-widget-editor__toolbar-left">
          <button
            type="button"
            className="inbx-btn inbx-btn--primary"
            disabled={errors.length > 0 || saving}
            onClick={() => void handleSave(false)}
          >
            Сохранить
          </button>
          <button
            type="button"
            className="inbx-btn inbx-btn--secondary"
            disabled={errors.length > 0 || saving}
            onClick={() => void handleSave(true)}
          >
            Сохранить и закрыть
          </button>
          {onOpenPreviewPage && (
            <button type="button" className="inbx-btn inbx-btn--ghost" onClick={onOpenPreviewPage}>
              <i className="ti ti-external-link" /> Превью-страница
            </button>
          )}
        </div>
        <div className="inbx-widget-editor__toolbar-right">
          {errors.length > 0 && <span className="inbx-widget-editor__errors">{errors[0]}</span>}
          <button type="button" className="inbx-btn inbx-btn--ghost" onClick={onCancel} title="Закрыть">
            <i className="ti ti-x" /> Закрыть
          </button>
        </div>
      </div>

      <div className="inbx-widget-editor__split">
        <div className="inbx-widget-editor__left">
          <div className="inbx-widget-editor__tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`inbx-widget-editor__tab ${activeTab === t.key ? 'is-active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="inbx-widget-editor__tab-body">
            {body.renderTab(activeTab, draft, (patch) => onChange({ ...draft, ...patch }))}
          </div>
        </div>

        <div className="inbx-widget-editor__right">
          <div className="inbx-widget-editor__preview-bar">
            <span className="inbx-widget-editor__preview-label">Превью:</span>
            {states.map((s) => (
              <button
                key={s}
                type="button"
                className={`inbx-widget-editor__state-btn ${
                  previewState === s ? 'is-active' : ''
                }`}
                onClick={() => setPreviewState(s)}
              >
                {STATE_LABELS[s]}
              </button>
            ))}
          </div>
          <div className="inbx-widget-editor__preview-stage">
            <LivePreview draft={draft} state={previewState} />
          </div>
        </div>
      </div>
    </div>
  );
}

const STATE_LABELS: Record<PreviewState, string> = {
  collapsed: 'Свёрнут',
  notification: 'Уведомление',
  expanded: 'Развёрнут',
  success: 'Отправлено',
  offline: 'Оффлайн',
};
